/**
 * Cart Quantity Cap Engine
 * ------------------------
 * Replaces the Orichi Discount Limit app (per-product max quantities + group
 * total caps) with an event-driven, zero-polling implementation.
 *
 * Architecture:
 *   - Fetches config from the control panel once per page-load (5-min ETag-cached).
 *     Endpoint defaults to http://localhost:8000/api/cart-limits-config; override
 *     via `window.AGB_CART_LIMITS_CONFIG_URL` before this script loads.
 *   - One initial GET to /cart.js to seed the in-memory cart snapshot. Then
 *     the snapshot is updated event-driven via:
 *       - The script's own fetch interceptor (sees /cart/add response payload)
 *       - The theme's `cart:change` event (Prestige emits it on every modification)
 *   - For each group rule, fetches /collections/{handle}/products.json once
 *     (cached in sessionStorage per tab) to learn membership.
 *   - On a cart-add attempt, the interceptor checks the snapshot against rules
 *     BEFORE the request fires. If it would exceed any cap, the request is
 *     blocked and a small toast is shown. The shopper sees nothing in the cart.
 *
 * No setInterval. No polling. Zero background traffic after page load.
 *
 * Coexists with favorite-color-variant-picker.js's cart-add interceptor: both
 * wrap window.fetch with idempotency flags (.__cartCapAddWrapped /
 * .__cartCapWrapped) so neither doubles up.
 */

(function () {
    'use strict';

    if (typeof window === 'undefined') return;
    if (window.__AGB_CART_CAP_LOADED__) return;
    window.__AGB_CART_CAP_LOADED__ = true;

    // ===== Defaults (baked-in, enforce unconditionally) =======================
    // Bundled into the engine so caps apply even when the control-panel
    // backend is unreachable (production deploy, dev with backend stopped,
    // network outage, CORS misconfig, anything). The async fetchConfigFromAPI
    // below overlays panel-managed rules on top of these — admin edits in
    // the panel take precedence at runtime, but never AT THE COST OF having
    // no enforcement when the panel is down.
    //
    // MUST stay in sync with backend's CART_LIMITS_DEFAULTS. When you add a
    // new product cap in the panel and want it baked-in here too, just paste
    // the JSON line into productLimits. Bundle handles the merge.
    const FALLBACK_CONFIG = {
        schemaVersion: 1,
        enabled: true,
        blockMessageMode: 'toast',
        blockMessageText: 'לא ניתן לרכוש יותר מפריט אחד מסוג זה',
        groupBlockMessageText: 'לא ניתן לרכוש יותר פריטים מקטגוריה זו',
        clampExistingOnLoad: true,
        productLimits: [
            { handle: 'blood-red-scorpion-patch', max: 1, enabled: true, label: 'Blood Red Scorpion Patch' },
            { handle: 'free-easter-rabbit-scorpion-patch', max: 1, enabled: true, label: 'Free Easter Rabbit Scorpion Patch' },
            { handle: 'easter-rabbit-scorpion-patch', max: 1, enabled: true, label: 'Easter Rabbit Scorpion Patch' },
            { handle: 'agilite-free-easter-bunny-scorpion-patch', max: 1, enabled: true, label: 'Agilite Free Easter Bunny Scorpion Patch' },
            { handle: 'agilite-easter-rabbit-scorpion-patch', max: 1, enabled: true, label: 'Agilite Easter Rabbit Scorpion Patch' },
            { handle: 'agilite-easter-bunny-scorpion-patch', max: 1, enabled: true, label: 'Agilite Easter Bunny Scorpion Patch' },
            { handle: 'easter-bunny-scorpion-patch', max: 1, enabled: true, label: 'Easter Bunny Scorpion Patch' },
            { handle: 'free-builder-scorpion-patch', max: 1, enabled: true, label: 'Free Builder Scorpion Patch' },
            { handle: 'free-agilite-tourniquet-holder', max: 1, enabled: true, label: 'Free Agilite Tourniquet Holder' },
            { handle: 'santa-scorpion-ornament', max: 1, enabled: true, label: 'Santa Scorpion Ornament' },
            { handle: 'rear-pouch', max: 1, enabled: true, label: 'Rear Pouch' },
            { handle: 'hi-vis-yellow-scorpion-patch', max: 1, enabled: true, label: 'Hi-Vis Yellow Scorpion Patch' },
            { handle: 'glow-in-the-dark-scorpion-patch', max: 1, enabled: true, label: 'Glow in the Dark Scorpion Patch' },
            { handle: 'forest-green-scorpion-patch', max: 1, enabled: true, label: 'Forest Green Scorpion Patch' },
            { handle: 'testing-team-canada-hat', max: 1, enabled: true, label: 'Testing Team Canada Hat' },
            { handle: 'testing-team-alpha-hat', max: 1, enabled: true, label: 'Testing Team Alpha Hat' },
            { handle: 'testing-team-9-hat', max: 1, enabled: true, label: 'Testing Team 9 Hat' },
            { handle: 'testing-team-8-hat', max: 1, enabled: true, label: 'Testing Team 8 Hat' },
            { handle: 'testing-team-7-hat', max: 1, enabled: true, label: 'Testing Team 7 Hat' },
            { handle: 'testing-team-5-hat', max: 1, enabled: true, label: 'Testing Team 5 Hat' },
            { handle: 'testing-team-4-hat', max: 1, enabled: true, label: 'Testing Team 4 Hat' },
            { handle: 'testing-team-3-hat', max: 1, enabled: true, label: 'Testing Team 3 Hat' },
            { handle: 'testing-team-2-hat', max: 1, enabled: true, label: 'Testing Team 2 Hat' },
            { handle: 'testing-team-12-hat', max: 1, enabled: true, label: 'Testing Team 12 Hat' },
            { handle: 'testing-team-11-hat', max: 1, enabled: true, label: 'Testing Team 11 Hat' },
            { handle: 'testing-team-10-hat', max: 1, enabled: true, label: 'Testing Team 10 Hat' },
            { handle: 'gear-tester-shirt', max: 1, enabled: true, label: 'Gear Tester Shirt' },
            { handle: 'purple-scorpion-patch', max: 1, enabled: true, label: 'Purple Scorpion Patch' },
            { handle: 'agilite-free-glove-hanger', max: 1, enabled: true, label: 'Agilite Free Glove Hanger' },
            { handle: 'agilite-glove-hanger', max: 1, enabled: true, label: 'Agilite Glove Hanger' },
            { handle: 'navy-scorpion-patch', max: 1, enabled: true, label: 'Navy Scorpion Patch' },
            { handle: '4th-of-july-us-agilite-scorpion-patch', max: 1, enabled: true, label: '4th Of July US Agilite Scorpion Patch' },
            { handle: 'sar-orange-agilite-scorpion-patch', max: 1, enabled: true, label: 'SAR Orange Agilite Scorpion Patch' },
            { handle: 'testingteam-canada', max: 1, enabled: true, label: 'TestingTeam Canada' },
            { handle: 'testingteam-alpha', max: 1, enabled: true, label: 'TestingTeam Alpha' },
            { handle: 'testingteam-9', max: 1, enabled: true, label: 'TestingTeam 9' },
            { handle: 'testingteam-8', max: 1, enabled: true, label: 'TestingTeam 8' },
            { handle: 'testingteam-7', max: 1, enabled: true, label: 'TestingTeam 7' },
            { handle: 'testingteam-5', max: 1, enabled: true, label: 'TestingTeam 5' },
            { handle: 'testingteam-4', max: 1, enabled: true, label: 'TestingTeam 4' },
            { handle: 'testingteam-3', max: 1, enabled: true, label: 'TestingTeam 3' },
            { handle: 'testingteam-2', max: 1, enabled: true, label: 'TestingTeam 2' },
            { handle: 'testingteam-12', max: 1, enabled: true, label: 'TestingTeam 12' },
            { handle: 'testingteam-11', max: 1, enabled: true, label: 'TestingTeam 11' },
            { handle: 'testingteam-10', max: 1, enabled: true, label: 'TestingTeam 10' },
            { handle: 'testingteam-1', max: 1, enabled: true, label: 'TestingTeam 1' },
            { handle: 'glove-hanger', max: 1, enabled: true, label: 'Glove Hanger' },
            { handle: 'reaper-scorpion-patch', max: 1, enabled: true, label: 'Reaper Scorpion Patch' },
            { handle: 'santa-scorpion-patch', max: 1, enabled: true, label: 'Santa Scorpion Patch' },
            { handle: 'blackout-scorpion-patch', max: 1, enabled: true, label: 'Blackout Scorpion Patch' },
            { handle: 't-e-sub-zero-patch', max: 1, enabled: true, label: 'T&E Sub-Zero Patch' },
            { handle: '4th-of-july-us-scorpion-patch', max: 1, enabled: true, label: '4th Of July US Scorpion Patch' },
            { handle: 'sar-orange-scorpion-patch', max: 1, enabled: true, label: 'SAR Orange Scorpion Patch' },
            { handle: 'free-scorpion-patch', max: 1, enabled: true, label: 'Free Scorpion Patch' },
            { handle: 'cyber-scorpion-patch', max: 1, enabled: true, label: 'Cyber Scorpion Patch' },
            { handle: 't-e-testing-team-5-patch', max: 1, enabled: true, label: 'T&E Testing Team 5 Patch' },
            { handle: 't-e-testing-team-2-patch', max: 1, enabled: true, label: 'T&E Testing Team 2 Patch' },
            { handle: 't-e-warfighter-patch', max: 1, enabled: true, label: 'T&E Warfighter Patch' },
            { handle: 't-e-testing-team-3-patch', max: 1, enabled: true, label: 'T&E Testing Team 3 Patch' },
            { handle: 't-e-testing-team-11-patch', max: 1, enabled: true, label: 'T&E Testing Team 11 Patch' },
            { handle: 'six-pack-fanny-pack-conversion-strap', max: 1, enabled: true, label: 'Six Pack Fanny Pack Conversion Strap' },
            { handle: 't-e-k-zero-patch', max: 1, enabled: true, label: 'T&E K-Zero Patch' },
            { handle: 't-e-testing-team-brown-patch', max: 1, enabled: true, label: 'T&E Testing Team Brown Patch' },
            { handle: 't-e-testing-team-green-patch', max: 1, enabled: true, label: 'T&E Testing Team Green Patch' },
            { handle: 't-e-private-shooter-patch', max: 1, enabled: true, label: 'T&E Private Shooter Patch' },
            { handle: 't-e-military-patch', max: 1, enabled: true, label: 'T&E Military Patch' },
            { handle: 't-e-law-enforcement-patch', max: 1, enabled: true, label: 'T&E Law Enforcement Patch' },
            { handle: 'free-stickers-set', max: 1, enabled: true, label: 'Free Stickers Set' },
            { handle: 't-e-testing-team-patch', max: 1, enabled: true, label: 'T&E Testing Team Patch' },
            { handle: 'neon-green-scorpion-patch', max: 1, enabled: true, label: 'Neon Green Scorpion Patch' }
        ],
        groupLimits: [
            {
                name: 'Free Patches & Gifts',
                handles: [],
                collectionHandle: 'free-patches-gifts',
                maxTotal: 2,
                enabled: true
            }
        ]
    };

    // Per-page revalidation (0 = no client throttle): always issue the
    // conditional GET on each load so a panel change applies on the shopper's
    // NEXT navigation, not up to 5 min later. Cheap 304 (If-None-Match ETag)
    // when unchanged; the synchronous cache-first load is separate, so no
    // flicker. Was 5*60*1000; see reference_panel_config_propagation.
    const CONFIG_TTL_MS = 0;
    // (Left at 30 min: which products are in a group's collection changes rarely
    // and each miss is a Shopify collection fetch, not the panel config.)
    const COLLECTION_TTL_MS = 30 * 60 * 1000;
    // Live vs Preview storefront detection (mirrored from
    // favorite-color-variant-picker.js). Each engine file is self-contained
    // so it can load in any order. See the long comment there for what each
    // branch does and why preview is the safe default.
    function _agbDetectStorefrontEnv() {
        try {
            const urlForce = new URLSearchParams(location.search).get('storefront_env');
            if (urlForce === 'preview' || urlForce === 'live') return urlForce;
        } catch (_) {}
        try {
            const stored = localStorage.getItem('AGB_StorefrontEnv');
            if (stored === 'preview' || stored === 'live') return stored;
        } catch (_) {}
        const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
        if (role === 'main') return 'live';
        if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
        const host = (location && location.hostname) || '';
        if (host.endsWith('.shopifypreview.com')) return 'preview';
        if (host.endsWith('.myshopify.com')) return 'preview';
        if (host === '127.0.0.1' || host === 'localhost') return 'preview';
        try {
            if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
        } catch (_) {}
        return 'preview';
    }

    const CART_LIMITS_ENV = _agbDetectStorefrontEnv();
    const LS_CONFIG_KEY = `AGB_CartLimitsConfig_${CART_LIMITS_ENV}`;
    const LS_CONFIG_ETAG = `AGB_CartLimitsConfigEtag_${CART_LIMITS_ENV}`;
    const LS_CONFIG_AT = `AGB_CartLimitsConfigFetchedAt_${CART_LIMITS_ENV}`;

    const state = {
        config: FALLBACK_CONFIG,
        // Map<variantId(string), {productId, productHandle, quantity, key}>
        // Plus product handle -> sum of all variant quantities for that product.
        snapshot: new Map(),
        productQuantity: new Map(),   // handle -> int (sum across variants)
        collectionMembers: new Map(), // collectionHandle -> Set<productHandle>
        // Tag-based rule matching support. Tags come from:
        //   1. window.Catalog.tagsOf(handle) — server-emitted via the
        //      catalog-data-emit Liquid snippet (sync, no network).
        //   2. /products/<handle>.json fallback — lazy fetch, cached in
        //      memory + sessionStorage so subsequent calls are sync.
        tagsCache: new Map(),         // handle -> string[] (lowercased)
        tagsCacheInflight: new Set(), // handle (fetch in progress)
        ready: false
    };

    // ===== Config loading =====================================================

    function loadCachedConfig() {
        try {
            const raw = localStorage.getItem(LS_CONFIG_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed && parsed.schemaVersion === 1) {
                state.config = mergeConfig(FALLBACK_CONFIG, parsed);
            }
        } catch (e) { /* ignore */ }
    }

    function mergeConfig(base, incoming) {
        return Object.assign({}, base, incoming);
    }

    async function fetchConfigFromAPI() {
        const base = (window.AGB_CART_LIMITS_CONFIG_URL)
            || 'http://localhost:8000/api/cart-limits-config';
        const endpoint = CART_LIMITS_ENV === 'preview'
            ? `${base}${base.indexOf('?') === -1 ? '?' : '&'}env=preview`
            : base;
        try {
            const lastFetched = parseInt(localStorage.getItem(LS_CONFIG_AT) || '0', 10);
            if (Date.now() - lastFetched < CONFIG_TTL_MS) return;

            const etag = localStorage.getItem(LS_CONFIG_ETAG);
            const headers = { 'Accept': 'application/json' };
            if (etag) headers['If-None-Match'] = etag;

            const res = await fetch(endpoint, { headers });
            localStorage.setItem(LS_CONFIG_AT, String(Date.now()));
            if (res.status === 304) return;
            if (!res.ok) return;

            const body = await res.json();
            if (!body || !body.config) return;
            const newCfg = body.config;
            if (newCfg.schemaVersion !== 1) return;

            localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(newCfg));
            const newEtag = res.headers.get('ETag');
            if (newEtag) localStorage.setItem(LS_CONFIG_ETAG, newEtag);

            state.config = mergeConfig(FALLBACK_CONFIG, newCfg);
            // Config changed: re-resolve collection membership for any groups
            // that reference a collection, and re-check existing cart for
            // clamp violations.
            preloadCollectionMemberships();
            if (state.config.clampExistingOnLoad) {
                clampExistingCartIfNeeded();
            }
        } catch (e) { /* network failure: keep cached config */ }
    }

    // ===== Cart snapshot ======================================================

    function rebuildProductQuantityMap() {
        state.productQuantity.clear();
        for (const item of state.snapshot.values()) {
            const h = item.productHandle;
            if (!h) continue;
            state.productQuantity.set(h, (state.productQuantity.get(h) || 0) + item.quantity);
        }
    }

    function applyCartPayload(cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        state.snapshot.clear();
        for (const it of cart.items) {
            const vid = String(it.variant_id);
            const productTitle = it.product_title || it.title || '';
            state.snapshot.set(vid, {
                productId: it.product_id,
                productHandle: it.handle || it.product_handle || '',
                productTitle: productTitle,
                productTitleStem: stripColorFromTitle(productTitle),
                quantity: it.quantity || 0,
                key: it.key
            });
        }
        rebuildProductQuantityMap();
        // Warm tags for tag-based rule matching. Fire-and-forget; the
        // first decideAdd may not have tags yet (handled by the post-
        // response reactive pass), but subsequent operations do.
        try { preloadSnapshotTags(); } catch (e) {}
    }

    /**
     * Strip a known color from the end of a product title. Used by the
     * title-based matching tier so "Rear Pouch Multicam" and "Rear Pouch
     * Black" both reduce to "Rear Pouch" -- they get treated as the same
     * item for capping purposes even when their Shopify handles are wildly
     * different. Case-insensitive, multi-word colors handled (e.g.
     * "Ranger Green", "Multicam Black"). If no known color trails, the
     * title is returned unchanged.
     */
    function stripColorFromTitle(title) {
        if (!title) return '';
        let stripped = String(title).trim();
        const sortedSlugs = (typeof KNOWN_COLOR_SLUGS !== 'undefined' ? KNOWN_COLOR_SLUGS : [])
            .slice().sort((a, b) => b.length - a.length);
        for (const slug of sortedSlugs) {
            // slug-form -> title-case phrase: 'ranger-green' -> 'Ranger Green'
            const phrase = slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            const pattern = new RegExp('\\s*' + phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i');
            if (pattern.test(stripped)) {
                stripped = stripped.replace(pattern, '').trim();
                break;
            }
        }
        // Normalize: lowercase + collapse whitespace so comparisons are stable.
        return stripped.toLowerCase().replace(/\s+/g, ' ').trim();
    }

    async function fetchInitialCart() {
        try {
            const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
            if (!r.ok) return;
            const cart = await r.json();
            applyCartPayload(cart);
            state.ready = true;
            if (state.config.clampExistingOnLoad) {
                clampExistingCartIfNeeded();
            }
        } catch (e) { /* offline / WAF challenge: snapshot stays empty */ }
    }

    // ===== Tag-based rule matching ============================================
    //
    // Cart rules can scope by Shopify product.tags in addition to handle.
    // For each cart line we need to know the line's product tags. Two
    // sources, both warmed lazily:
    //
    //   1. window.Catalog.tagsOf(handle) — populated server-side via
    //      snippets/catalog-data-emit.liquid for every product rendered
    //      on the current page. Fast, sync, no network.
    //   2. /products/<handle>.json — fallback for products not rendered
    //      through product-card.liquid (rare, e.g. some recommendation
    //      widgets). Cached in state.tagsCache + sessionStorage; the
    //      first call kicks off a fetch and returns null, subsequent
    //      calls hit the cache synchronously.

    const TAGS_CACHE_PREFIX = 'AGB_CartCap_tags2:';

    // Dead-handle cache for /products/<h>.js 404s. AGB's variant +
    // bundle inference fetches component handles (pincer, md2-compact-
    // trauma, amap-iii-assault, agilite-free-glove-hanger) that exist in
    // the AGB component model but aren't published Shopify products.
    // First hit per tab logs the 404 (Chromium prints network 404s
    // unconditionally); subsequent hits hit the cache and never go to
    // the network, so no more console noise.
    const DEAD_HANDLE_KEY = 'AGB_CartCap_deadProductHandles';
    let _deadProductHandles = null;

    function _loadDeadProductHandles() {
        if (_deadProductHandles) return _deadProductHandles;
        _deadProductHandles = new Set();
        try {
            const raw = sessionStorage.getItem(DEAD_HANDLE_KEY);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) {
                    for (const h of arr) {
                        if (typeof h === 'string' && h) _deadProductHandles.add(h);
                    }
                }
            }
        } catch (e) { /* swallow */ }
        return _deadProductHandles;
    }

    function _isDeadProductHandle(handle) {
        if (!handle) return false;
        return _loadDeadProductHandles().has(handle);
    }

    function _markDeadProductHandle(handle) {
        if (!handle) return;
        const set = _loadDeadProductHandles();
        if (set.has(handle)) return;
        set.add(handle);
        try {
            sessionStorage.setItem(DEAD_HANDLE_KEY, JSON.stringify(Array.from(set)));
        } catch (e) { /* sessionStorage full */ }
    }

    // Expose the dead-handle predicate as a window-level function so AGB
    // engine code (agb-variant-extension.js) can short-circuit its
    // /products/<h>.js fetches BEFORE making the network call — that
    // way we don't even log a synthetic 404 from the fetch-wrapper path,
    // we just never fire the fetch. Returns false when the handle has
    // never been seen as dead in this tab's sessionStorage.
    try {
        if (typeof window !== 'undefined') {
            window.AGB_CartCap_isDeadHandle = _isDeadProductHandle;
        }
    } catch (e) {}

    function tagsForHandle(handle) {
        if (!handle) return null;
        // Source 1: window.Catalog
        try {
            if (window.Catalog && typeof window.Catalog.tagsOf === 'function') {
                if (typeof window.Catalog.get === 'function' && window.Catalog.get(handle)) {
                    // Product known to catalog -> trust its tags (may be []).
                    const tags = window.Catalog.tagsOf(handle) || [];
                    return tags.map(function (t) { return String(t).toLowerCase().trim(); }).filter(Boolean);
                }
            }
        } catch (e) { /* fall through */ }
        // Source 2: tagsCache (lazy /products/<h>.json)
        if (state.tagsCache.has(handle)) {
            return state.tagsCache.get(handle);
        }
        // Source 2 cold path: try sessionStorage
        try {
            const raw = sessionStorage.getItem(TAGS_CACHE_PREFIX + handle);
            if (raw != null) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    const lower = parsed.map(function (t) { return String(t).toLowerCase().trim(); }).filter(Boolean);
                    state.tagsCache.set(handle, lower);
                    return lower;
                }
            }
        } catch (e) { /* swallow */ }
        // Kick off async fetch so the next call can return synchronously.
        fetchProductTagsAsync(handle);
        return null;
    }

    async function fetchProductTagsAsync(handle) {
        if (!handle) return;
        if (state.tagsCache.has(handle)) return;
        if (state.tagsCacheInflight.has(handle)) return;
        state.tagsCacheInflight.add(handle);
        try {
            const r = await fetch('/products/' + encodeURIComponent(handle) + '.json', {
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' }
            });
            if (!r.ok) {
                state.tagsCache.set(handle, []);
                try { sessionStorage.setItem(TAGS_CACHE_PREFIX + handle, '[]'); } catch (e) {}
                return;
            }
            const data = await r.json();
            const p = data && data.product;
            // ⚠ /products/<h>.json returns tags as a COMMA-SEPARATED STRING
            // ("Discount Sale, free, group limit of two"), not an array — the
            // array-only check silently produced [] for every product on this
            // path, i.e. for exactly the HIDDEN products (not in Catalog) that
            // need it (found 2026-07-14). Accept both shapes; splitting on
            // commas only, so tags WITH SPACES stay intact.
            let rawTags = p && p.tags;
            if (typeof rawTags === 'string') rawTags = rawTags.split(',');
            const tags = Array.isArray(rawTags)
                ? rawTags.map(function (t) { return String(t).toLowerCase().trim(); }).filter(Boolean)
                : [];
            state.tagsCache.set(handle, tags);
            try { sessionStorage.setItem(TAGS_CACHE_PREFIX + handle, JSON.stringify(tags)); } catch (e) {}
        } catch (e) {
            // Network failure: leave uncached so a later call retries.
        } finally {
            state.tagsCacheInflight.delete(handle);
        }
    }

    // True iff any of `ruleTags` matches any of the product's tags for
    // the given handle. Case-insensitive. Returns false when the line's
    // tags aren't known yet (the async fetch was kicked off) — the
    // post-response reactive enforcement pass will catch any miss once
    // the cache warms up.
    function lineHandleMatchesAnyTag(lineHandle, ruleTags) {
        if (!Array.isArray(ruleTags) || !ruleTags.length) return false;
        const itemTags = tagsForHandle(lineHandle);
        if (!itemTags || !itemTags.length) return false;
        for (const rt of ruleTags) {
            const needle = String(rt || '').toLowerCase().trim();
            if (!needle) continue;
            if (itemTags.indexOf(needle) !== -1) return true;
        }
        return false;
    }

    // Warm the tags cache for every cart line. Called after applyCartPayload
    // so rule matching at the next decideAdd / enforceRulesAfterModification
    // call can resolve tags synchronously.
    function preloadSnapshotTags() {
        for (const item of state.snapshot.values()) {
            if (!item.productHandle) continue;
            // tagsForHandle kicks off the fetch as a side effect when the
            // handle isn't in catalog and isn't cached.
            tagsForHandle(item.productHandle);
        }
    }

    // ===== Color-variant aware rule matching ==================================
    //
    // Many Agilite catalog products are stored as SEPARATE PRODUCTS per color
    // (not as variants of one base product). So a single visual "Rear Pouch"
    // is actually multiple Shopify products: rear-pouch-multicam,
    // rear-pouch-ranger-green, etc. -- each with its own handle.
    //
    // A rule on `rear-pouch` should cap ALL of those colors together (you can
    // only own one Rear Pouch, regardless of color). To match that intent,
    // the engine treats a rule's handle as matching any line whose handle is
    // either:
    //   - exactly the rule handle (covers products where colors ARE variants), OR
    //   - the rule handle + '-' + <recognized color slug> (covers the
    //     separate-product-per-color setup)
    //
    // KNOWN_COLOR_SLUGS mirrors the main engine's PRIORITY_COLORS + OOS
    // palette (slugified) plus a few common additions. Add new colors here
    // when the catalog adds them. Multi-word colors are listed first so the
    // greedy match catches "multicam-black" before "black".

    const KNOWN_COLOR_SLUGS = [
        'multicam-black', 'ranger-green', 'coyote-tan', 'coyote-brown',
        'm81-woodland', 'wolf-grey', 'olive-drab', 'sar-orange',
        'forest-green', 'blood-red', 'navy', 'purple', 'cyber',
        'hi-vis-yellow', 'glow-in-the-dark', 'blackout',
        'multicam', 'woodland', 'black', 'tan', 'green', 'brown',
        'grey', 'gray', 'olive', 'red', 'blue', 'orange', 'yellow'
    ];

    function isKnownColorSlug(slug) {
        if (!slug) return false;
        return KNOWN_COLOR_SLUGS.indexOf(slug) !== -1;
    }

    /**
     * Does this rule handle match this cart-line handle for capping purposes?
     * Exact match always wins; otherwise rule.handle is treated as a stem
     * and we check whether the line handle equals rule + '-' + <color>.
     * One-way: rule (stem) -> line (specific). A rule on a specific color
     * never matches a different specific color.
     */
    function ruleMatchesHandle(ruleHandle, lineHandle) {
        if (!ruleHandle || !lineHandle) return false;
        if (ruleHandle === lineHandle) return true;
        if (lineHandle.indexOf(ruleHandle + '-') !== 0) return false;
        const suffix = lineHandle.slice(ruleHandle.length + 1);
        return isKnownColorSlug(suffix);
    }

    /** Return ALL enabled product rules that match this line handle (handle, tag, or product_id). */
    function rulesMatchingHandle(lineHandle, productId) {
        if (!lineHandle && !productId) return [];
        const out = [];
        const pidStr = productId != null ? String(productId) : null;
        for (const r of (state.config.productLimits || [])) {
            if (!r || r.enabled === false) continue;
            // Tier 1+2: handle (exact or color-stem). Skip when rule has
            // no handle (tag-only rules).
            if (r.handle && ruleMatchesHandle(r.handle, lineHandle)) { out.push(r); continue; }
            // Tier 5: tag-based. Rule has productTags and the line's
            // product carries any of them.
            if (Array.isArray(r.productTags) && r.productTags.length
                && lineHandleMatchesAnyTag(lineHandle, r.productTags)) {
                out.push(r);
                continue;
            }
            // Tier 3: if any snapshot line that matches this rule by handle
            // shares product_id with our target, we're a variant of the
            // same Shopify product.
            if (pidStr && r.handle) {
                let pidHit = false;
                for (const other of state.snapshot.values()) {
                    if (!other.productId || String(other.productId) !== pidStr) continue;
                    if (ruleMatchesHandle(r.handle, other.productHandle)) {
                        pidHit = true;
                        break;
                    }
                }
                if (pidHit) { out.push(r); continue; }
            }
        }
        return out;
    }

    /** Diagnostic logger — silenced. Flip DEBUG to true to re-enable. */
    var DEBUG = false;
    function dbg() {
        if (!DEBUG) return;
        try { console.log.apply(console, [].slice.call(arguments)); } catch (e) {}
    }
    function warn() {
        try { console.warn.apply(console, [].slice.call(arguments)); } catch (e) {}
    }

    /**
     * Convert rule.handle (kebab-case) to a normalized title-stem to match
     * against the same normalization applied to cart-line product titles.
     * Used by Tier 4 matching (label/title-based).
     *
     * Example: handle 'rear-pouch' -> 'rear pouch'
     *          rule.label 'Rear Pouch' -> stripColorFromTitle returns 'rear pouch'
     *          cart line product_title 'Rear Pouch Multicam' -> stem 'rear pouch'
     * All three normalize to the same string, so the rule matches both
     * 'Rear Pouch Multicam' and 'Rear Pouch Black' lines.
     */
    function ruleTitleStems(rule) {
        const stems = [];
        if (rule.label) {
            const s = stripColorFromTitle(rule.label);
            if (s) stems.push(s);
        }
        // Handle as a phrase: 'rear-pouch' -> 'rear pouch'
        if (rule.handle) {
            const phrase = rule.handle.replace(/-/g, ' ').toLowerCase().trim();
            if (phrase) stems.push(stripColorFromTitle(phrase));
        }
        return stems;
    }

    /**
     * Sum current quantities across every snapshot line that matches this
     * rule. FOUR-tier matching to cover every common storefront setup:
     *   1. Exact handle match (handle === rule.handle)
     *   2. Color-stem match (rule.handle is prefix; suffix in KNOWN_COLOR_SLUGS)
     *   3. Same product_id as any already-matched line
     *   4. Same product_title stem (color stripped) as any matched line, OR
     *      matching the rule's own label/handle title stem directly.
     *      This is the bulletproof fallback when handles are completely
     *      unrelated but the products share a visible name in the cart.
     */
    function currentQtyForRule(rule) {
        let sum = 0;
        const matchedProductIds = new Set();
        const matchedTitleStems = new Set();
        const ruleStems = ruleTitleStems(rule);
        const ruleTags = Array.isArray(rule.productTags) ? rule.productTags : [];

        // For per-product rules: a tag-scoped rule treats EACH matching
        // product as a separate cap of `rule.max`. So we sum each
        // matching product's lines independently and return the MAX
        // current quantity across them (any over-cap product triggers
        // the violation). This mirrors how handle-based per-product
        // rules apply per-handle.
        if (ruleTags.length && !rule.handle) {
            let maxPerProduct = 0;
            const byHandle = new Map(); // handle -> sum
            for (const item of state.snapshot.values()) {
                if (!lineHandleMatchesAnyTag(item.productHandle, ruleTags)) continue;
                const h = item.productHandle || '';
                byHandle.set(h, (byHandle.get(h) || 0) + (item.quantity || 0));
            }
            for (const v of byHandle.values()) {
                if (v > maxPerProduct) maxPerProduct = v;
            }
            return maxPerProduct;
        }

        // Pass 1: handle-based + title-based + tag-based against rule's own scope.
        for (const item of state.snapshot.values()) {
            let matched = false;
            if (rule.handle && ruleMatchesHandle(rule.handle, item.productHandle)) {
                matched = true;
            } else if (item.productTitleStem && ruleStems.indexOf(item.productTitleStem) !== -1) {
                matched = true;
            } else if (ruleTags.length && lineHandleMatchesAnyTag(item.productHandle, ruleTags)) {
                matched = true;
            }
            if (matched) {
                sum += item.quantity || 0;
                if (item.productId) matchedProductIds.add(String(item.productId));
                if (item.productTitleStem) matchedTitleStems.add(item.productTitleStem);
            }
        }

        // Pass 2: sibling matching by product_id or title-stem.
        if (matchedProductIds.size || matchedTitleStems.size) {
            for (const item of state.snapshot.values()) {
                if (rule.handle && ruleMatchesHandle(rule.handle, item.productHandle)) continue; // already counted
                if (item.productTitleStem && ruleStems.indexOf(item.productTitleStem) !== -1) continue; // already counted
                if (ruleTags.length && lineHandleMatchesAnyTag(item.productHandle, ruleTags)) continue; // already counted
                const pidMatch = item.productId && matchedProductIds.has(String(item.productId));
                const titleMatch = item.productTitleStem && matchedTitleStems.has(item.productTitleStem);
                if (pidMatch || titleMatch) sum += item.quantity || 0;
            }
        }
        return sum;
    }

    /** Does this snapshot line match the rule via any of the five tiers? */
    function lineMatchesRule(rule, item) {
        if (!rule || !item) return false;
        // Tier 1+2: handle (exact or color-stem)
        if (rule.handle && ruleMatchesHandle(rule.handle, item.productHandle)) return true;
        // Tier 5: tag match.
        if (Array.isArray(rule.productTags) && rule.productTags.length
            && lineHandleMatchesAnyTag(item.productHandle, rule.productTags)) {
            return true;
        }
        // Tier 4 against rule's own stems
        const ruleStems = ruleTitleStems(rule);
        if (item.productTitleStem && ruleStems.indexOf(item.productTitleStem) !== -1) return true;
        // Tier 3 + 4 sibling: same product_id or same title-stem as another
        // matched line in the snapshot.
        const targetId = item.productId ? String(item.productId) : null;
        const targetTitle = item.productTitleStem || '';
        for (const other of state.snapshot.values()) {
            if (other === item) continue;
            const handleMatch = rule.handle && ruleMatchesHandle(rule.handle, other.productHandle);
            const titleStemMatch = other.productTitleStem && ruleStems.indexOf(other.productTitleStem) !== -1;
            if (!handleMatch && !titleStemMatch) continue;
            // 'other' is in the rule's set. Is 'item' a sibling?
            if (targetId && String(other.productId) === targetId) return true;
            if (targetTitle && other.productTitleStem === targetTitle) return true;
        }
        return false;
    }

    /** Does any handle in this group's membership (exact, color-stemmed, collection, or tag) match this line? */
    function groupMatchesHandle(group, lineHandle) {
        if (!lineHandle) return false;
        const members = groupMembersUnion(group);
        if (members.has(lineHandle)) return true;
        // Color-variant fallback: if any group member is a stem of this line.
        for (const h of members) {
            if (ruleMatchesHandle(h, lineHandle)) return true;
        }
        // Reverse collection membership via the server-emitted catalog. This is
        // the publish-independent path: /collections/<h>/products.json can't
        // see hidden / unpublished free-gift products, but the catalog (emitted
        // in Liquid for every product rendered on the page) can. So if this
        // cart line's product declares membership in the group's collection,
        // the group applies — even when the Ajax member list came back empty.
        try {
            if (group.collectionHandle && window.Catalog && typeof window.Catalog.collectionsOf === 'function') {
                const cols = window.Catalog.collectionsOf(lineHandle) || [];
                if (cols.indexOf(group.collectionHandle) !== -1) return true;
            }
        } catch (e) { /* catalog not ready: fall through */ }
        // Tag-based membership.
        if (Array.isArray(group.productTags) && group.productTags.length
            && lineHandleMatchesAnyTag(lineHandle, group.productTags)) {
            return true;
        }
        return false;
    }

    /** Sum quantities in cart that belong to this group. Uses the SAME
     *  predicate as the add-time check (groupMatchesHandle) so "does the group
     *  apply to this add" and "how much is already in the group" can never
     *  disagree — including the color-stem, catalog-collection, and tag tiers. */
    function currentQtyForGroup(group) {
        let sum = 0;
        for (const item of state.snapshot.values()) {
            const h = item.productHandle;
            if (!h) continue;
            if (groupMatchesHandle(group, h)) sum += item.quantity || 0;
        }
        return sum;
    }

    // ===== Post-response reactive enforcement =================================
    //
    // Preventive intercept (in wrapFetch) can miss violations when the
    // shopper adds a variant whose handle our snapshot doesn't know yet.
    // E.g. shopper has rear-pouch-multicam in cart (snapshot knows it),
    // adds rear-pouch-black (new variant_id, snapshot doesn't know its
    // handle). The preventive check sees no matching rule and lets it
    // through.
    //
    // This function runs AFTER the cart modification responds. It re-reads
    // the freshly-updated snapshot, finds any rule that's now over its
    // cap, and fires a compensating /cart/change.js to clamp the
    // most-recently-added items back. The over-cap item is in the cart
    // for ~100-500ms before getting removed, but the shopper never sees
    // it because most carts don't visually re-render until cart:change
    // (which fires after the compensation). Toast confirms what happened.

    let _originalFetch = null; // captured by wrapFetch for compensation calls

    async function enforceRulesAfterModification(addedOrChangedItems) {
        if (!state.config.enabled) return false;

        // Find all violations by scanning every rule against the snapshot.
        const violations = [];

        for (const rule of (state.config.productLimits || [])) {
            if (!rule || rule.enabled === false) continue;
            const sum = currentQtyForRule(rule);
            if (sum > rule.max) {
                violations.push({ kind: 'product', rule, overage: sum - rule.max });
                const ruleId = rule.handle || ('tags=' + (rule.productTags || []).join(','));
                warn('VIOLATION product rule', ruleId, 'sum=', sum, '> max=', rule.max);
            }
        }

        for (const group of (state.config.groupLimits || [])) {
            if (!group || group.enabled === false) continue;
            if (group.collectionHandle && !state.collectionMembers.has(group.collectionHandle)) {
                await fetchCollectionMembers(group.collectionHandle);
            }
            const sum = currentQtyForGroup(group);
            if (sum > group.maxTotal) {
                violations.push({ kind: 'group', group, overage: sum - group.maxTotal });
                warn('VIOLATION group', group.name, 'sum=', sum, '> max=', group.maxTotal);
            }
        }

        if (!violations.length) {
            dbg('no violations after modification');
            return false;
        }

        // Build a queue of candidate lines to reduce, in priority order:
        //   1. Lines from addedOrChangedItems (the request that just fired)
        //   2. All other cart lines in INSERTION ORDER REVERSED (newest first)
        // This way we always reduce the just-added items first, then fall back
        // to whichever line is newest if the touched-items list didn't catch
        // the right one (e.g. parsing failure).
        const candidateVariantIds = [];
        const seen = new Set();
        for (const item of (addedOrChangedItems || []).slice().reverse()) {
            if (item && item.variantId) {
                const v = String(item.variantId);
                if (!seen.has(v)) { candidateVariantIds.push(v); seen.add(v); }
            }
        }
        // Then everything else, newest cart line first (snapshot iterates in
        // insertion order, which Shopify maintains as oldest -> newest).
        const allVids = Array.from(state.snapshot.keys());
        for (let i = allVids.length - 1; i >= 0; i -= 1) {
            const v = allVids[i];
            if (!seen.has(v)) { candidateVariantIds.push(v); seen.add(v); }
        }

        let anyReduced = false;
        for (const vid of candidateVariantIds) {
            const line = state.snapshot.get(vid);
            if (!line) continue;
            const handle = line.productHandle;

            // How much to reduce: enough to clear the most-violated rule
            // this line participates in, capped at the line's current qty.
            let maxReduce = 0;
            for (const v of violations) {
                if (v.kind === 'product' && lineMatchesRule(v.rule, line)) {
                    maxReduce = Math.max(maxReduce, v.overage);
                } else if (v.kind === 'group' && groupMatchesHandle(v.group, handle)) {
                    maxReduce = Math.max(maxReduce, v.overage);
                }
            }
            if (maxReduce <= 0) continue;

            const newQty = Math.max(0, (line.quantity || 0) - maxReduce);
            // Use line.key (the unique lineKey) not the variantId. Shopify
            // accepts BOTH for /cart/change but variantId can be ambiguous
            // when the cart has multiple lines for the same variant (selling
            // plans, line properties, bundles) -- and on some Shopify versions
            // an ambiguous {id: <variantId>, quantity: 0} REMOVES ALL MATCHING
            // LINES instead of just one, which is how "both items get deleted"
            // can happen even though only Black should be removed.
            const targetId = line.key || vid;
            warn('REDUCING line', { handle, variantId: vid, lineKey: line.key, targetId, fromQty: line.quantity, toQty: newQty });

            try {
                const fetchFn = _originalFetch || window.fetch.bind(window);
                const r = await fetchFn('/cart/change.js', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ id: targetId, quantity: newQty })
                });
                if (r && r.ok) {
                    const newCart = await r.json();
                    if (newCart && Array.isArray(newCart.items)) {
                        // Safeguard: if the server removed MORE lines than we
                        // asked for (e.g. ambiguous variantId hit two lines),
                        // log loudly so we can spot it. Don't try to re-add --
                        // that creates cascading state. Just surface the bug.
                        const expectedRemoval = (newQty === 0) ? 1 : 0;
                        const actualRemoval = state.snapshot.size - newCart.items.length;
                        if (actualRemoval > expectedRemoval) {
                            warn('SERVER REMOVED MORE LINES THAN EXPECTED!', {
                                expected: expectedRemoval,
                                actual: actualRemoval,
                                snapshotBefore: Array.from(state.snapshot.values()).map(v => ({ handle: v.productHandle, qty: v.quantity, key: v.key })),
                                cartAfter: newCart.items.map(i => ({ handle: i.handle, qty: i.quantity, key: i.key }))
                            });
                        }
                        applyCartPayload(newCart);
                    }
                    anyReduced = true;
                }
            } catch (e) { warn('reduce request failed', e); }

            // Re-evaluate violations after each reduction
            const stillViolating = [];
            for (const v of violations) {
                if (v.kind === 'product') {
                    const sum = currentQtyForRule(v.rule);
                    if (sum > v.rule.max) stillViolating.push({ ...v, overage: sum - v.rule.max });
                } else {
                    const sum = currentQtyForGroup(v.group);
                    if (sum > v.group.maxTotal) stillViolating.push({ ...v, overage: sum - v.group.maxTotal });
                }
            }
            violations.length = 0;
            violations.push(...stillViolating);
            if (!violations.length) break;
        }

        if (anyReduced) {
            // Two GET-only fetches (no write requests that could mutate cart):
            //   1. /cart.js -> the cart object (items, totals, etc.)
            //   2. /?sections=<id> -> the rendered drawer HTML
            // Combined into the detail.cart.sections[<id>] shape Prestige's
            // CartDrawer.onCartChange_fn expects (reads cart.sections[fullId]).
            //
            // The cart-drawer section is wrapped inside the overlay-group
            // section group, so its real id is
            // "sections--<groupSchemaId>__cart-drawer" -- NOT bare "cart-drawer".
            // Fetching ?sections=cart-drawer returns {} on these themes.
            // Read the actual id from the DOM via getCartDrawerSectionKey().
            //
            // NEVER use POST /cart/update.js {updates: {}} for "safe refetch"
            // -- Shopify's older interpretation treats empty updates as
            // "set all line quantities to 0" which empties the entire cart.
            const fetchFn = _originalFetch || window.fetch.bind(window);
            const sectionKey = getCartDrawerSectionKey();
            let cart = null;
            let sectionHtml = '';
            try {
                const r = await fetchFn('/cart.js', {
                    credentials: 'same-origin',
                    headers: { 'Accept': 'application/json' }
                });
                if (r && r.ok) cart = await r.json();
            } catch (e) { warn('cart refetch failed after compensation', e); }
            if (sectionKey) {
                try {
                    // The drawer is rendered in the language of the URL it is fetched from, so
                    // it is fetched from the page's own locale root (/de/, /fr/...): from '/' a
                    // German page got an English drawer back (2026-10-05).
                    const r2 = await fetchFn(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + '?sections=' + encodeURIComponent(sectionKey), {
                        credentials: 'same-origin',
                        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
                    });
                    if (r2 && r2.ok) {
                        const data = await r2.json();
                        sectionHtml = (data && data[sectionKey]) || '';
                    }
                } catch (e) { warn('section refetch failed after compensation', e); }
            } else {
                warn('cart-drawer section id not discoverable from DOM; skipping drawer refresh');
            }

            // Swap the drawer DOM with fresh HTML from the section API.
            if (sectionKey && sectionHtml) {
                try {
                    const wrapper = document.getElementById('shopify-section-' + sectionKey);
                    if (wrapper) wrapper.innerHTML = sectionHtml;
                } catch (e) { warn('drawer DOM swap failed', e); }
            }

            // Build the cart-with-sections payload Prestige expects:
            // event.detail.cart.sections[<fullSectionId>] = html
            const sectionsMap = {};
            if (sectionKey && sectionHtml) sectionsMap[sectionKey] = sectionHtml;
            const cartDetail = Object.assign({}, cart || { items: [], item_count: 0 }, {
                sections: sectionsMap
            });

            try {
                document.dispatchEvent(new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { cart: cartDetail, source: 'cart-cap-enforcement' }
                }));
            } catch (e) { warn('cart:change dispatch failed', e); }

            showToast(state.config.blockMessageText);
        } else if (violations.length) {
            warn('Failed to resolve violations; rules may not match the cart-line handles.');
        }
        return anyReduced;
    }

    /**
     * Discover the live cart-drawer section id by reading the DOM. Prestige
     * mounts cart-drawer inside the overlay-group section group, so the real
     * id is "sections--<groupSchemaId>__cart-drawer" (e.g.
     * "sections--21530953416956__cart-drawer"), not bare "cart-drawer".
     * Falls back to anything matching the cart-drawer suffix.
     */
    function getCartDrawerSectionKey() {
        const el = document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]')
            || document.querySelector('[id^="shopify-section-"][id*="cart-drawer"]');
        if (!el) {
            const cd = document.querySelector('cart-drawer');
            if (cd) {
                let p = cd.parentElement;
                while (p && p !== document.body) {
                    if (p.id && p.id.indexOf('shopify-section-') === 0) {
                        return p.id.replace('shopify-section-', '');
                    }
                    p = p.parentElement;
                }
            }
            return '';
        }
        return el.id.replace('shopify-section-', '');
    }

    /**
     * Re-fetch the cart-drawer section's rendered HTML and swap it into the
     * DOM so the visible UI matches the post-compensation cart state.
     * Without this, the shopper sees the just-added-then-removed line stuck
     * in the drawer even though it's gone from the server cart.
     */
    async function refreshCartDrawerDom() {
        try {
            const sectionKey = getCartDrawerSectionKey();
            if (!sectionKey) return;
            const fetchFn = _originalFetch || window.fetch.bind(window);
            const r = await fetchFn(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + '?sections=' + encodeURIComponent(sectionKey), {
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
            });
            if (!r || !r.ok) return;
            const data = await r.json();
            const html = data && data[sectionKey];
            if (!html) return;

            const wrapper = document.getElementById('shopify-section-' + sectionKey);
            if (!wrapper) return;
            wrapper.innerHTML = html;
        } catch (e) { warn('drawer refresh failed', e); }
    }

    // ===== Collection membership resolution ===================================

    async function fetchCollectionMembers(collectionHandle) {
        if (!collectionHandle) return new Set();
        if (state.collectionMembers.has(collectionHandle)) {
            return state.collectionMembers.get(collectionHandle);
        }
        // sessionStorage cache
        try {
            const cached = sessionStorage.getItem(`AGB_CartLimitsCollection:${collectionHandle}`);
            if (cached) {
                const obj = JSON.parse(cached);
                if (obj && obj.fetchedAt && (Date.now() - obj.fetchedAt) < COLLECTION_TTL_MS && Array.isArray(obj.handles)) {
                    const s = new Set(obj.handles);
                    state.collectionMembers.set(collectionHandle, s);
                    return s;
                }
            }
        } catch (e) { /* swallow */ }

        try {
            // Shopify Ajax: paginated, 250/page max. Most "limit collections"
            // (free patches, gifts) are << 250 items, so one page suffices.
            const r = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`, {
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' }
            });
            if (!r.ok) {
                state.collectionMembers.set(collectionHandle, new Set());
                return state.collectionMembers.get(collectionHandle);
            }
            const data = await r.json();
            const handles = (data && Array.isArray(data.products))
                ? data.products.map(p => p.handle).filter(Boolean)
                : [];
            if (!handles.length) {
                // The Ajax endpoint only returns products PUBLISHED to the
                // Online Store sales channel. A "free patches & gifts"
                // collection of hidden products comes back empty here — the
                // group then relies entirely on window.Catalog membership
                // (see groupMembersUnion / groupMatchesHandle). Surface it so
                // a silent zero-enforcement is diagnosable.
                warn('[cart-cap] collection "' + collectionHandle + '" returned 0 products from ' +
                     '/collections/' + collectionHandle + '/products.json (hidden/unpublished products are ' +
                     'invisible to this endpoint). Falling back to window.Catalog membership.');
            }
            const s = new Set(handles);
            state.collectionMembers.set(collectionHandle, s);
            try {
                sessionStorage.setItem(
                    `AGB_CartLimitsCollection:${collectionHandle}`,
                    JSON.stringify({ fetchedAt: Date.now(), handles })
                );
            } catch (e) { /* sessionStorage full */ }
            return s;
        } catch (e) {
            state.collectionMembers.set(collectionHandle, new Set());
            return state.collectionMembers.get(collectionHandle);
        }
    }

    function preloadCollectionMemberships() {
        if (!state.config.enabled) return;
        for (const g of state.config.groupLimits || []) {
            if (g && g.enabled !== false && g.collectionHandle) {
                fetchCollectionMembers(g.collectionHandle);
            }
        }
    }

    function groupMembersUnion(group) {
        const set = new Set();
        if (Array.isArray(group.handles)) {
            for (const h of group.handles) if (h) set.add(h);
        }
        if (group.collectionHandle) {
            // Source 1: Shopify Ajax /collections/<h>/products.json (published only).
            const cached = state.collectionMembers.get(group.collectionHandle);
            if (cached) for (const h of cached) set.add(h);
            // Source 2: server-emitted catalog (window.Catalog). Knows the
            // collection's members for every product rendered on the page —
            // including hidden / unpublished free-gift products the Ajax
            // endpoint omits. No network, no GraphQL.
            try {
                if (window.Catalog && typeof window.Catalog.productsInCollection === 'function') {
                    const fromCatalog = window.Catalog.productsInCollection(group.collectionHandle) || [];
                    for (const h of fromCatalog) if (h) set.add(h);
                }
            } catch (e) { /* catalog not ready */ }
        }
        return set;
    }

    function groupCurrentTotal(group) {
        const members = groupMembersUnion(group);
        let total = 0;
        for (const [handle, qty] of state.productQuantity.entries()) {
            if (members.has(handle)) total += qty;
        }
        return total;
    }

    // ===== Rule lookups =======================================================

    function findProductLimit(handle) {
        if (!handle) return null;
        // Exact-handle match wins.
        for (const r of state.config.productLimits || []) {
            if (!r || r.enabled === false) continue;
            if (r.handle && r.handle === handle) return r;
        }
        // Tag-scoped fallback: lowest max wins (most restrictive) so the
        // existing-cart clamp does the safest thing.
        let best = null;
        for (const r of state.config.productLimits || []) {
            if (!r || r.enabled === false) continue;
            if (!Array.isArray(r.productTags) || !r.productTags.length) continue;
            if (!lineHandleMatchesAnyTag(handle, r.productTags)) continue;
            if (!best || r.max < best.max) best = r;
        }
        return best;
    }

    function findHandleForVariant(variantId) {
        const it = state.snapshot.get(String(variantId));
        return it ? it.productHandle : null;
    }

    /**
     * Get the product handle of the current product page from the URL.
     * Returns null if not on a /products/<handle> page. Critical fallback
     * for decideAdd when adding a fresh variant that isn't in the cart yet:
     * without this, rulesMatchingHandle returns [] for the unknown variant,
     * the add passes the preventive check, the item lands in the cart, and
     * the reactive compensation has to delete it -- which is more fragile
     * than just blocking up-front.
     */
    function getCurrentPageProductHandle() {
        try {
            const m = String(window.location.pathname || '').match(/\/products\/([^\/?#]+)/);
            return m ? m[1] : null;
        } catch (e) { return null; }
    }

    /**
     * Look up the product handle from any product form in the DOM whose
     * variant input matches the given variantId. Catches quick-shop modals,
     * recommendation cards, and any other off-PDP add-to-cart surface.
     */
    function getFormProductHandle(variantId) {
        if (!variantId) return null;
        const vidStr = String(variantId);
        try {
            const forms = document.querySelectorAll('form[action*="/cart/add"]');
            for (const f of forms) {
                const idInput = f.querySelector('input[name="id"]');
                if (!idInput) continue;
                if (String(idInput.value) !== vidStr) continue;
                // Found the form for this variant. Walk up looking for any
                // element carrying the product handle.
                const candidates = [
                    f.getAttribute('data-product-handle'),
                    f.dataset && f.dataset.productHandle,
                    f.closest('[data-product-handle]') && f.closest('[data-product-handle]').getAttribute('data-product-handle')
                ];
                for (const c of candidates) {
                    if (c) return String(c);
                }
                // Last-ditch: form action like /products/<handle>/cart -- rare
                const action = f.getAttribute('action') || '';
                const m = action.match(/\/products\/([^\/?#]+)/);
                if (m) return m[1];
            }
        } catch (e) { /* swallow */ }
        return null;
    }

    function currentQtyForHandle(handle) {
        return state.productQuantity.get(handle) || 0;
    }

    // ===== Block decision =====================================================

    /**
     * Returns {blocked: true, reason, max} when the add would violate a rule,
     * or {blocked: false} when it's allowed. Resolves any needed collection
     * memberships asynchronously.
     */
    async function decideAdd(items) {
        if (!state.config.enabled) return { blocked: false };

        // If the snapshot isn't ready or doesn't know any of the variants
        // we're adding, refresh once before deciding. Same off-by-one fix
        // as decideUpdate -- without this, the first add after page load
        // sees current=0 even when the cart already has 1 of the item.
        if (!state.ready) {
            await fetchInitialCart();
        } else {
            const unknownVariant = items.some(it => !findHandleForVariant(it.variantId));
            if (unknownVariant) await fetchInitialCart();
        }

        for (const item of items) {
            // Resolve handle, falling back through several sources because the
            // variant may not be in the cart snapshot yet (first add of a new
            // variant): cached cart line -> current PDP page handle -> DOM-discovered
            // form handle. Without a handle the per-product rule can't match
            // and the add slips through, requiring destructive compensation.
            let handle = item.handle
                || findHandleForVariant(item.variantId)
                || getCurrentPageProductHandle()
                || getFormProductHandle(item.variantId);
            const qtyDelta = item.quantity || 1;
            const snapLine = item.variantId ? state.snapshot.get(String(item.variantId)) : null;
            const productId = snapLine ? snapLine.productId : null;
            dbg('decideAdd resolving handle', { variantId: item.variantId, handle, productId });

            // Warm tags for the to-be-added product before evaluating rules.
            // tagsForHandle returns null on cold cache; await the fetch so
            // tag-scoped rules get a synchronous answer during the rule
            // check below. Pure no-op when the catalog already knows the
            // product (Catalog.tagsOf is sync).
            if (handle && tagsForHandle(handle) == null) {
                try { await fetchProductTagsAsync(handle); } catch (e) {}
            }

            // Per-product cap (handle + color-stem + tag + product_id matching).
            const matchingRules = rulesMatchingHandle(handle, productId);
            dbg('decideAdd', { variantId: item.variantId, handle, productId, matchingRules: matchingRules.map(r => r.handle) });
            for (const rule of matchingRules) {
                const current = currentQtyForRule(rule);
                if (current + qtyDelta > rule.max) {
                    return {
                        blocked: true,
                        reason: 'product',
                        max: rule.max,
                        handle,
                        ruleHandle: rule.handle,
                        message: state.config.blockMessageText
                    };
                }
            }

            // Group caps (also color-aware: a group containing "rear-pouch"
            // counts every rear-pouch-<color> too).
            for (const group of (state.config.groupLimits || [])) {
                if (!group || group.enabled === false) continue;
                // Make sure collection members are loaded (no-op if cached)
                if (group.collectionHandle && !state.collectionMembers.has(group.collectionHandle)) {
                    await fetchCollectionMembers(group.collectionHandle);
                }
                if (!groupMatchesHandle(group, handle)) continue;

                const groupCurrent = currentQtyForGroup(group);
                if (groupCurrent + qtyDelta > group.maxTotal) {
                    return {
                        blocked: true,
                        reason: 'group',
                        groupName: group.name,
                        max: group.maxTotal,
                        message: state.config.groupBlockMessageText
                    };
                }
            }
        }
        return { blocked: false };
    }

    // ===== Banner =============================================================
    //
    // Uses Prestige's existing `.banner.banner--error` class for visual parity
    // with the theme-rendered cart-error banner that appears on PDP when
    // /cart/add returns 422 (theme.js's onCartError_fn). For the cart-drawer
    // +/- button and other non-PDP block surfaces, there's no product-form
    // cart:error path -- we render the same banner ourselves at top of viewport
    // so the shopper sees exactly ONE styled message regardless of surface.

    function showToast(text) {
        if (!state.config.enabled) return;
        if (state.config.blockMessageMode !== 'toast') return;
        let host = document.getElementById('agb-cart-cap-banner-host');
        if (!host) {
            host = document.createElement('div');
            host.id = 'agb-cart-cap-banner-host';
            host.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999999;pointer-events:none;';
            document.body.appendChild(host);
        }
        // One banner at a time -- replace any in-flight one with the latest.
        host.innerHTML = '';
        const banner = document.createElement('div');
        banner.className = 'banner banner--error';
        banner.setAttribute('role', 'alert');
        banner.style.cssText = 'text-align:center;font-weight:600;pointer-events:auto;';
        banner.textContent = text || 'Limit reached.';
        host.appendChild(banner);
        setTimeout(() => { if (banner.parentNode === host) host.removeChild(banner); }, 3500);
    }

    // ===== Parsing add-to-cart payloads =======================================

    function parseAddPayload(init) {
        // Shopify accepts form-encoded or JSON. We need {variantId, quantity}[]
        if (!init || !init.body) return [];
        const body = init.body;
        try {
            if (typeof body === 'string') {
                // Try JSON
                try {
                    const obj = JSON.parse(body);
                    return normalizeJsonAdd(obj);
                } catch (_) {
                    // Form-encoded
                    return normalizeFormAdd(body);
                }
            }
            if (body instanceof FormData) {
                const obj = {};
                for (const [k, v] of body.entries()) obj[k] = v;
                return normalizeJsonAdd(obj);
            }
            if (body instanceof URLSearchParams) {
                return normalizeFormAdd(body.toString());
            }
        } catch (e) { /* swallow */ }
        return [];
    }

    function normalizeJsonAdd(obj) {
        if (!obj) return [];
        if (Array.isArray(obj.items)) {
            return obj.items
                .filter(i => i && (i.id != null))
                .map(i => ({ variantId: String(i.id), quantity: Number(i.quantity || 1) }));
        }
        if (obj.id != null) {
            return [{ variantId: String(obj.id), quantity: Number(obj.quantity || 1) }];
        }
        return [];
    }

    function normalizeFormAdd(s) {
        try {
            const p = new URLSearchParams(s);
            const id = p.get('id');
            const q = Number(p.get('quantity') || 1);
            if (!id) return [];
            return [{ variantId: String(id), quantity: q }];
        } catch (e) { return []; }
    }

    // ===== Parsing /cart/change and /cart/update payloads =====================
    //
    // /cart/change.js: replaces a single line's quantity.
    //   Shapes (any of):
    //     { id: <variantId|lineKey>, quantity: N }
    //     { line: <1-based-index>, quantity: N }
    //   The + button in the cart drawer fires this on every increment.
    //
    // /cart/update.js: batch updates a whole cart.
    //   Shape: { updates: { <variantIdOrLineKey>: N, ... } }
    //
    // For both, we resolve each line being SET to N and check the cap
    // against the resulting cart, NOT against the current state. So + from
    // qty 1 -> 2 is checked as "would line have qty 2 after this".

    function parseCartChangePayload(init) {
        if (!init || !init.body) return [];
        const body = init.body;
        try {
            let raw;
            if (typeof body === 'string') {
                try { raw = JSON.parse(body); } catch (_) {
                    const p = new URLSearchParams(body);
                    raw = {};
                    for (const [k, v] of p.entries()) raw[k] = v;
                }
            } else if (body instanceof FormData) {
                raw = {};
                for (const [k, v] of body.entries()) raw[k] = v;
            } else if (body instanceof URLSearchParams) {
                raw = {};
                for (const [k, v] of body.entries()) raw[k] = v;
            } else if (typeof body === 'object') {
                raw = body;
            }
            if (!raw) return [];

            const targetQty = Number(raw.quantity != null ? raw.quantity : NaN);
            if (!Number.isFinite(targetQty)) return [];

            // Resolve which line this update targets.
            //   raw.id  -> variantId or lineKey
            //   raw.line -> 1-based index into cart.items (legacy form)
            let variantId = null;
            let lineKey = null;
            if (raw.id != null) {
                const idStr = String(raw.id);
                // Heuristic: numeric = variantId, alphanumeric with colons = lineKey
                if (/^\d+$/.test(idStr)) variantId = idStr;
                else lineKey = idStr;
            } else if (raw.line != null) {
                const idx = Number(raw.line) - 1; // 1-based
                if (Number.isInteger(idx) && idx >= 0) {
                    // Resolve from snapshot in insertion order (mirrors Shopify's array order)
                    let i = 0;
                    for (const item of state.snapshot.values()) {
                        if (i === idx) { variantId = String(item.productId ? item.productId : ''); lineKey = item.key; break; }
                        i += 1;
                    }
                }
            }
            if (!variantId && !lineKey) return [];
            return [{ variantId, lineKey, targetQty }];
        } catch (e) { return []; }
    }

    function parseCartUpdatePayload(init) {
        if (!init || !init.body) return [];
        try {
            let raw;
            if (typeof init.body === 'string') {
                raw = JSON.parse(init.body);
            } else if (init.body instanceof FormData) {
                raw = {};
                for (const [k, v] of init.body.entries()) raw[k] = v;
            } else if (typeof init.body === 'object') {
                raw = init.body;
            }
            if (!raw) return [];

            const updates = raw.updates || raw;
            const out = [];
            if (typeof updates === 'object' && updates && !Array.isArray(updates)) {
                for (const key of Object.keys(updates)) {
                    const targetQty = Number(updates[key]);
                    if (!Number.isFinite(targetQty)) continue;
                    const isVariantId = /^\d+$/.test(key);
                    out.push({
                        variantId: isVariantId ? key : null,
                        lineKey: isVariantId ? null : key,
                        targetQty
                    });
                }
            } else if (Array.isArray(updates)) {
                // Legacy: updates can be a positional array; index = line - 1.
                let idx = 0;
                for (const v of updates) {
                    const targetQty = Number(v);
                    if (!Number.isFinite(targetQty)) { idx += 1; continue; }
                    let i = 0;
                    for (const item of state.snapshot.values()) {
                        if (i === idx) { out.push({ variantId: null, lineKey: item.key, targetQty }); break; }
                        i += 1;
                    }
                    idx += 1;
                }
            }
            return out;
        } catch (e) { return []; }
    }

    // Resolve update target -> snapshot line. Falls back to variantId match.
    function findSnapshotLine(target) {
        if (target.variantId && state.snapshot.has(target.variantId)) {
            return { variantId: target.variantId, ...state.snapshot.get(target.variantId) };
        }
        if (target.lineKey) {
            for (const [vid, item] of state.snapshot.entries()) {
                if (item.key === target.lineKey) return { variantId: vid, ...item };
            }
        }
        return null;
    }

    /**
     * For cart/change and cart/update: determine if applying the requested
     * quantities would violate any cap. Returns {blocked, ...} same as decideAdd.
     *
     * Critical correctness note: if any target line isn't in our snapshot,
     * we MUST refetch /cart.js synchronously before deciding. Otherwise the
     * first + click after page-load lands while the snapshot is empty,
     * findSnapshotLine returns null, we `continue` past the rule check, and
     * the change goes through. The block only kicks in on the SECOND click
     * (after the first response refreshed the snapshot). One-shot refresh
     * fixes the off-by-one.
     */
    async function decideUpdate(updates) {
        if (!state.config.enabled) return { blocked: false };

        // Refresh the cart snapshot if any target line is missing. The initial
        // fetchInitialCart() on page load might still be in flight, or might
        // have failed (network, WAF challenge). Either way, deciding against
        // an empty/stale snapshot is wrong.
        let needsRefresh = false;
        for (const u of updates) {
            if (!findSnapshotLine(u)) { needsRefresh = true; break; }
        }
        if (needsRefresh || !state.ready) {
            await fetchInitialCart();
        }

        // Collect the line-level changes we're about to apply, with the
        // resolved per-line {handle, before, after, delta}.
        const touches = []; // {handle, before, after, delta}

        for (const u of updates) {
            const line = findSnapshotLine(u);
            if (!line || !line.productHandle) {
                // Still couldn't resolve after refresh -- conservative fallback:
                // if the requested targetQty exceeds the tightest possible cap
                // (max=1 is the most common), still block. We don't know which
                // product, so use a generic message.
                const tightestMax = Math.min(...(state.config.productLimits || [])
                    .filter(r => r && r.enabled !== false)
                    .map(r => r.max));
                if (Number.isFinite(tightestMax) && u.targetQty > tightestMax) {
                    return {
                        blocked: true,
                        reason: 'unknown-line',
                        max: tightestMax,
                        message: state.config.blockMessageText
                    };
                }
                continue;
            }
            const before = line.quantity || 0;
            const after = u.targetQty;
            const delta = after - before;
            if (delta <= 0) continue; // shrinking or no-op never violates
            touches.push({ handle: line.productHandle, productId: line.productId, before, after, delta });
        }

        if (!touches.length) return { blocked: false };

        // Per-product cap (handle + color-stem + product_id matching). For each
        // touched line, find all matching rules and project: rule's current SUM
        // across siblings, MINUS this line's before, PLUS this line's after.
        for (const touch of touches) {
            const matchingRules = rulesMatchingHandle(touch.handle, touch.productId);
            for (const rule of matchingRules) {
                const projected = currentQtyForRule(rule) - touch.before + touch.after;
                if (projected > rule.max) {
                    return {
                        blocked: true,
                        reason: 'product',
                        max: rule.max,
                        handle: touch.handle,
                        ruleHandle: rule.handle,
                        message: state.config.blockMessageText
                    };
                }
            }
        }

        // Group cap (color-aware). Project: current group sum minus the
        // touched line's before, plus its after. If sum > maxTotal, block.
        for (const group of (state.config.groupLimits || [])) {
            if (!group || group.enabled === false) continue;
            if (group.collectionHandle && !state.collectionMembers.has(group.collectionHandle)) {
                await fetchCollectionMembers(group.collectionHandle);
            }
            // Only enforce when at least one touched line is in this group.
            const touchedInGroup = touches.filter(t => groupMatchesHandle(group, t.handle));
            if (!touchedInGroup.length) continue;

            let projTotal = currentQtyForGroup(group);
            for (const t of touchedInGroup) {
                projTotal = projTotal - t.before + t.after;
            }
            if (projTotal > group.maxTotal) {
                return {
                    blocked: true,
                    reason: 'group',
                    groupName: group.name,
                    max: group.maxTotal,
                    message: state.config.groupBlockMessageText
                };
            }
        }
        return { blocked: false };
    }

    // ===== Existing-cart clamp ===============================================

    async function clampExistingCartIfNeeded() {
        if (!state.config.enabled) return;
        if (!state.config.clampExistingOnLoad) return;
        if (!state.ready) return;

        const updates = {};
        for (const [vid, item] of state.snapshot.entries()) {
            const rule = findProductLimit(item.productHandle);
            if (rule && item.quantity > rule.max) {
                updates[item.key || vid] = rule.max;
            }
        }
        if (Object.keys(updates).length) {
            try {
                await fetch('/cart/update.js', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ updates })
                });
                // Re-fetch to refresh the snapshot from the source of truth.
                await fetchInitialCart();
            } catch (e) { /* swallow */ }
        }

        // GROUP clamp on load (2026-07-14): reuse the post-modification enforcer,
        // which already scans group/collection rules (fetching collection
        // membership as needed) and reduces the NEWEST lines first, with the
        // shopper-facing toast. Before this, group violations in an EXISTING
        // cart were only blocked from further adds and never reduced — a cart
        // already over a collection cap (items added before the rule existed,
        // batch adds, or a bypass path) stayed over the cap forever (Dorian
        // reproduced: 4 items of a max-2 collection persisting on ROW).
        try { await enforceRulesAfterModification([]); } catch (e) { /* swallow */ }
    }

    // ===== Fetch interceptor ==================================================

    function blockedResponse(message) {
        const body = JSON.stringify({
            status: 422,
            message: message,
            description: message,
            cart_cap_blocked: true
        });
        return new Response(body, {
            status: 422,
            statusText: 'Cart Limit Reached',
            headers: { 'Content-Type': 'application/json' }
        });
    }

    function wrapFetch() {
        if (typeof window.fetch !== 'function') return;
        if (window.fetch.__cartCapWrapped) return;

        const original = window.fetch.bind(window);
        _originalFetch = original; // for compensation calls in enforceRulesAfterModification

        const wrapped = async function (input, init) {
            const url = typeof input === 'string' ? input : (input && input.url) || '';

            // Dead-handle short-circuit for /products/<handle>.js fetches.
            // AGB's variant + bundle inference code fetches /products/<h>.js
            // for component handles like pincer, md2-compact-trauma,
            // amap-iii-assault, agilite-free-glove-hanger — handles that
            // exist in the AGB component model but aren't published as
            // standalone Shopify products, so every call returns 404 and
            // floods the console. We remember the dead handles in
            // sessionStorage and synthesize a 404 Response without going
            // to the network (the browser console only logs network 404s,
            // not synthetic ones, so this silences the noise after the
            // first hit per tab).
            if (url) {
                const deadMatch = /\/products\/([^/?#]+)\.js(?:\?|$)/.exec(url);
                if (deadMatch) {
                    const productHandle = deadMatch[1];
                    if (_isDeadProductHandle(productHandle)) {
                        return new Response('{"error":"product not found (cached)"}', {
                            status: 404,
                            statusText: 'Not Found (cached)',
                            headers: { 'Content-Type': 'application/json' }
                        });
                    }
                    // Otherwise pass through, but observe the response so
                    // a fresh 404 gets marked dead for the next call.
                    return original(input, init).then((res) => {
                        if (res && res.status === 404) _markDeadProductHandle(productHandle);
                        return res;
                    });
                }
            }

            if (state.config.enabled && url) {
                try {
                    // /cart/add  -- enforce on add (new line or qty bump).
                    // Don't show our own banner here: theme.js's product-form
                    // sees the 422 from blockedResponse and dispatches
                    // cart:error, which buy-buttons.onCartError_fn renders
                    // as a .banner.banner--error inside the buy-buttons element
                    // on PDP. Showing both would double-message.
                    if (url.indexOf('/cart/add') !== -1) {
                        const items = parseAddPayload(init);
                        if (items.length) {
                            const decision = await decideAdd(items);
                            if (decision.blocked) {
                                return blockedResponse(decision.message);
                            }
                        }
                    }
                    // /cart/change  -- the cart-drawer +/- button. Sets a line
                    // to an exact quantity. Block if the target qty exceeds cap.
                    else if (url.indexOf('/cart/change') !== -1) {
                        const updates = parseCartChangePayload(init);
                        if (updates.length) {
                            const decision = await decideUpdate(updates);
                            if (decision.blocked) {
                                showToast(decision.message || 'לא ניתן לרכוש יותר מפריט אחד מסוג זה');
                                return blockedResponse(decision.message);
                            }
                        }
                    }
                    // /cart/update  -- batch update from a cart-page form.
                    else if (url.indexOf('/cart/update') !== -1) {
                        const updates = parseCartUpdatePayload(init);
                        if (updates.length) {
                            const decision = await decideUpdate(updates);
                            if (decision.blocked) {
                                showToast(decision.message || 'לא ניתן לרכוש יותר מפריט אחד מסוג זה');
                                return blockedResponse(decision.message);
                            }
                        }
                    }
                } catch (e) { /* fall through to real fetch on internal errors */ }
            }

            // Pass through and observe the response for snapshot updates +
            // reactive enforcement.
            //
            // NOTE on ERE: HTTP failure reporting (5xx / non-422 4xx /
            // network) used to live here. Moved to the global
            // shopify-purchase-watchdog in error-reporter.js so the same
            // coverage applies regardless of which script issued the
            // /cart/add request (theme.js, buy-buttons, custom builder,
            // etc.). This wrapper now focuses purely on cap enforcement.
            const isCartWrite = url && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
            const response = await original(input, init);
            try {
                if (isCartWrite && response.ok) {
                    // Capture the items touched by this request so the
                    // post-response enforcer knows which lines to reduce
                    // if a rule was violated.
                    let touchedItems = [];
                    try {
                        if (url.indexOf('/cart/add') !== -1) {
                            touchedItems = parseAddPayload(init);
                        } else if (url.indexOf('/cart/change') !== -1) {
                            touchedItems = parseCartChangePayload(init).map(u => ({
                                variantId: u.variantId, quantity: u.targetQty
                            }));
                        } else if (url.indexOf('/cart/update') !== -1) {
                            touchedItems = parseCartUpdatePayload(init).map(u => ({
                                variantId: u.variantId, quantity: u.targetQty
                            }));
                        }
                    } catch (e) { /* swallow */ }

                    response.clone().json().then(async data => {
                        // Bulletproof: ALWAYS refetch /cart.js after a cart-write
                        // to get authoritative state. Even when response carries
                        // a `cart.items` array, the response can lag the actual
                        // server state if a cart-add was followed by a quick
                        // /cart/change in flight. One extra GET vs. silent
                        // misenforcement -- worth it.
                        try {
                            const fetchFn = _originalFetch || original;
                            const r = await fetchFn('/cart.js', {
                                credentials: 'same-origin',
                                headers: { 'Accept': 'application/json' }
                            });
                            if (r && r.ok) {
                                const cart = await r.json();
                                if (cart && Array.isArray(cart.items)) {
                                    applyCartPayload(cart);
                                    dbg('snapshot after', url, 'lines:', Array.from(state.snapshot.values()).map(v => ({
                                        handle: v.productHandle,
                                        productId: v.productId,
                                        qty: v.quantity
                                    })));
                                }
                            }
                        } catch (e) { warn('cart refetch failed', e); }

                        // Always run reactive enforcement on cart writes,
                        // even when touchedItems is empty (parsing might
                        // have failed). The scan looks at the FULL snapshot,
                        // not just the touched items.
                        if (state.config.enabled) {
                            try { await enforceRulesAfterModification(touchedItems); }
                            catch (e) { warn('enforce failed', e); }
                        }
                    }).catch(() => { /* not JSON (e.g. /cart/clear with 204) -- ignore */ });
                }
            } catch (e) { /* swallow */ }
            return response;
        };

        wrapped.__cartCapWrapped = true;
        window.fetch = wrapped;
    }

    /**
     * Some legacy cart paths (and a few cart-app plugins) still use jQuery.ajax,
     * which under the hood uses XMLHttpRequest -- not fetch. Wrap XHR's open
     * + send so we catch /cart/add, /cart/change, /cart/update through there too.
     * Idempotent via the same window.__AGB_CART_CAP_LOADED__ guard at module top.
     */
    function wrapXHR() {
        if (typeof window.XMLHttpRequest !== 'function') return;
        const proto = window.XMLHttpRequest.prototype;
        if (proto.__cartCapWrappedXHR) return;
        proto.__cartCapWrappedXHR = true;

        const origOpen = proto.open;
        const origSend = proto.send;

        proto.open = function (method, url) {
            this.__cartCapUrl = url || '';
            this.__cartCapMethod = (method || '').toUpperCase();
            return origOpen.apply(this, arguments);
        };

        proto.send = function (body) {
            const xhr = this;
            const url = xhr.__cartCapUrl || '';
            const isCartWrite = state.config.enabled && url && (
                url.indexOf('/cart/add') !== -1 ||
                url.indexOf('/cart/change') !== -1 ||
                url.indexOf('/cart/update') !== -1
            );
            if (!isCartWrite || xhr.__cartCapMethod === 'GET') {
                return origSend.apply(xhr, arguments);
            }

            // Run the same decision logic; if blocked, short-circuit the XHR
            // with a synthetic 422 (matching what jQuery.ajax expects on error).
            const init = { body: body };
            const decisionPromise = (
                url.indexOf('/cart/add') !== -1 ? decideAdd(parseAddPayload(init)) :
                url.indexOf('/cart/change') !== -1 ? decideUpdate(parseCartChangePayload(init)) :
                decideUpdate(parseCartUpdatePayload(init))
            );

            decisionPromise.then(decision => {
                if (!decision || !decision.blocked) {
                    return origSend.apply(xhr, arguments);
                }
                // Same rationale as the fetch /cart/add path: on /cart/add the
                // theme's cart:error path renders a banner near the buy button,
                // so we'd double-message if we also rendered our own. Only show
                // our banner for /cart/change and /cart/update where no theme
                // banner fires.
                if (url.indexOf('/cart/add') === -1) {
                    showToast(decision.message || 'לא ניתן לרכוש יותר מפריט אחד מסוג זה');
                }

                // Simulate a completed XHR with status 422 so jQuery.ajax
                // runs its error callback; the cart UI then reverts the
                // optimistic quantity update.
                try {
                    Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
                    Object.defineProperty(xhr, 'status', { value: 422, configurable: true });
                    Object.defineProperty(xhr, 'statusText', { value: 'Cart Limit Reached', configurable: true });
                    Object.defineProperty(xhr, 'responseText', {
                        value: JSON.stringify({
                            status: 422,
                            message: decision.message,
                            description: decision.message,
                            cart_cap_blocked: true
                        }),
                        configurable: true
                    });
                    Object.defineProperty(xhr, 'response', { value: xhr.responseText, configurable: true });
                } catch (e) { /* property already non-configurable; just dispatch */ }

                if (typeof xhr.onreadystatechange === 'function') {
                    try { xhr.onreadystatechange(); } catch (e) {}
                }
                if (typeof xhr.onload === 'function') {
                    try { xhr.onload(); } catch (e) {}
                }
                if (typeof xhr.onerror === 'function') {
                    try { xhr.onerror(); } catch (e) {}
                }
                xhr.dispatchEvent(new Event('readystatechange'));
                xhr.dispatchEvent(new Event('load'));
                xhr.dispatchEvent(new Event('loadend'));
            }).catch(() => {
                // If decision logic throws, let the original request go through.
                try { origSend.apply(xhr, arguments); } catch (e) {}
            });
        };
    }

    function wireCartChangeListener() {
        // Prestige emits `cart:change` on adds/removes/quantity updates from
        // the native cart drawer. Re-pull the cart to keep the snapshot in
        // sync; cheap because /cart.js is small and Shopify caches it well.
        document.addEventListener('cart:change', () => {
            fetchInitialCart();
        });
    }

    /**
     * The cart page's own <form action="/cart" method="POST"> is a NATIVE
     * browser submit -- it never goes through fetch or XHR, so the two
     * interceptors above cannot see it. Pressing Enter in a quantity field
     * posts `updates[]` straight to Shopify and every cap is skipped.
     *
     * Measured on the live store 2026-08-16: a plain form POST set a max-1 $0
     * patch to 4 and Shopify accepted it. Order #289784 is the shipped version
     * of that hole (2 of a free patch capped at 1).
     *
     * Two deliberate limits here:
     *   - The CHECKOUT button lives in this same form and is never BLOCKED:
     *     blocking a real checkout is worse than one over-cap cart. Since
     *     2026-10-22 its typed quantities are lowered to the caps first
     *     (clampFormForCheckout), then checkout goes on; the cart-quantity-cap
     *     Shopify Function stays the server-side backstop.
     *   - Fail-OPEN: any throw, and the form submits exactly as it would have.
     */
    // ===== Checkout clamp ======================================================
    //
    // The Check out button posts the cart form's typed quantities (updates[])
    // together with the checkout request, so a number typed on /cart reaches
    // Shopify unchecked. Before that post leaves, every line is lowered to what
    // the caps allow, with the SAME matching the add/change checks use:
    //   - a product rule counts all its lines together (colour siblings share
    //     one cap); a tag-only rule caps each tagged product on its own, as
    //     currentQtyForRule does;
    //   - a group's lines together stay within maxTotal.
    // Lines are lowered from the bottom of the page's list up. Nothing is
    // blocked: the shopper goes on to checkout with the lowered quantities.
    // The server-side backstop remains the cart-quantity-cap checkout rule.

    const CHECKOUT_CLAMP_MS = 3000;

    // Lower the list's quantities (from its end) until they add up to `max`.
    function lowerToTotal(list, max) {
        let total = 0;
        for (const l of list) total += l.qty;
        if (total <= max) return false;
        for (let i = list.length - 1; i >= 0 && total > max; i--) {
            const cut = Math.min(list[i].qty, total - max);
            list[i].qty -= cut;
            total -= cut;
        }
        return true;
    }

    /**
     * Rewrite the form's quantity fields to fit the caps. Returns the message
     * to show when something was lowered, or null. `abandoned()` turns true
     * once checkout has already been sent (the time limit ran out): the form
     * is then left alone.
     */
    async function clampFormForCheckout(form, abandoned) {
        if (!state.config.enabled) return null;
        const inputs = Array.from(form.querySelectorAll('input[name="updates[]"]'));
        if (!inputs.length) return null;

        // Every line on the page has to be known before deciding.
        const unknown = inputs.some((el) => !findSnapshotLine({ lineKey: el.getAttribute('data-line-key') }));
        if (unknown || !state.ready) await fetchInitialCart();
        for (const g of (state.config.groupLimits || [])) {
            if (g && g.enabled !== false && g.collectionHandle && !state.collectionMembers.has(g.collectionHandle)) {
                await fetchCollectionMembers(g.collectionHandle);
            }
        }
        if (abandoned()) return null;

        const lines = [];
        for (const el of inputs) {
            const line = findSnapshotLine({ lineKey: el.getAttribute('data-line-key') });
            const typed = Math.floor(Number(el.value));
            if (!line || !line.productHandle || !Number.isFinite(typed)) continue;
            lines.push({ el, line, qty: Math.max(0, typed) });
        }
        if (!lines.length) return null;

        let productLowered = false;
        let groupLowered = false;

        for (const rule of (state.config.productLimits || [])) {
            if (!rule || rule.enabled === false) continue;
            const max = Number(rule.max);
            if (!Number.isFinite(max) || max < 0) continue;
            const perProduct = Array.isArray(rule.productTags) && rule.productTags.length && !rule.handle;
            const sets = new Map();
            for (const l of lines) {
                if (!lineMatchesRule(rule, l.line)) continue;
                const key = perProduct ? l.line.productHandle : '*';
                if (!sets.has(key)) sets.set(key, []);
                sets.get(key).push(l);
            }
            for (const set of sets.values()) {
                if (lowerToTotal(set, max)) productLowered = true;
            }
        }

        for (const group of (state.config.groupLimits || [])) {
            if (!group || group.enabled === false) continue;
            const max = Number(group.maxTotal);
            if (!Number.isFinite(max) || max < 0) continue;
            const members = lines.filter((l) => groupMatchesHandle(group, l.line.productHandle));
            if (lowerToTotal(members, max)) groupLowered = true;
        }

        let changed = false;
        for (const l of lines) {
            if (Number(l.el.value) !== l.qty) {
                l.el.value = String(l.qty);
                changed = true;
            }
        }
        if (!changed) return null;
        return (groupLowered && !productLowered)
            ? (state.config.groupBlockMessageText || state.config.blockMessageText)
            : state.config.blockMessageText;
    }

    // ===== Cart page quantity box and +/- links ================================
    //
    // ON THE CART PAGE THE THEME NAVIGATES (theme.js line-item-quantity,
    // changeLineItemQuantity: pageType "cart" -> window.location.href =
    // /cart/change?id=<key>&quantity=<n>). A typed number - on Enter, or when
    // the box loses focus, which is exactly what clicking Check out does - and
    // every +/- link go to Shopify as a plain page load that neither the fetch
    // nor the XHR wrapper sees (measured on the ROW preview 2026-10-22: 4 of a
    // max-1 patch reached checkout). These capture-phase listeners run before
    // the theme's own handlers on the line-item-quantity element and lower
    // the number to what the caps allow, so the theme navigates with that.
    // Already at the cap: the change is stopped and the box shows the cart's
    // quantity again. Fail-open: no snapshot yet, no rule, any throw -> the
    // theme does what it always did (and clamp-on-load + the checkout clamp
    // still apply).

    // The most this line may hold, given everything else in the cart; null
    // when nothing limits it or the cart is not known yet. Never below what
    // the line already holds - a cart that is over a cap is not made smaller
    // here (clamp-on-load does that), it just cannot grow.
    function allowedQtyForLine(lineKey) {
        if (!state.config.enabled || !state.ready) return null;
        const line = findSnapshotLine({ lineKey });
        if (!line || !line.productHandle) return null;
        const current = line.quantity || 0;
        const others = [];
        for (const item of state.snapshot.values()) {
            if (item.key !== lineKey) others.push(item);
        }
        let allowed = Infinity;
        let byGroup = false;
        for (const rule of rulesMatchingHandle(line.productHandle, line.productId)) {
            const max = Number(rule.max);
            if (!Number.isFinite(max)) continue;
            const perProduct = Array.isArray(rule.productTags) && rule.productTags.length && !rule.handle;
            let used = 0;
            for (const o of others) {
                if (perProduct ? o.productHandle === line.productHandle : lineMatchesRule(rule, o)) used += o.quantity || 0;
            }
            allowed = Math.min(allowed, max - used);
        }
        for (const group of (state.config.groupLimits || [])) {
            if (!group || group.enabled === false) continue;
            const max = Number(group.maxTotal);
            if (!Number.isFinite(max) || !groupMatchesHandle(group, line.productHandle)) continue;
            let used = 0;
            for (const o of others) {
                if (o.productHandle && groupMatchesHandle(group, o.productHandle)) used += o.quantity || 0;
            }
            if (max - used < allowed) { allowed = max - used; byGroup = true; }
        }
        if (allowed === Infinity) return null;
        return { allowed: Math.max(current, allowed), current, byGroup };
    }

    function onCartPage() {
        try {
            if (window.themeVariables && window.themeVariables.settings
                && window.themeVariables.settings.pageType === 'cart') return true;
            return /\/cart\/?$/.test(location.pathname);
        } catch (e) { return false; }
    }

    function wireCartPageQuantity() {
        // Typed into the quantity box.
        document.addEventListener('change', (event) => {
            try {
                const el = event.target;
                if (!el || !el.matches || !el.matches('input[data-line-key]') || !onCartPage()) return;
                const typed = Math.floor(Number(el.value));
                if (!Number.isFinite(typed)) return;
                const cap = allowedQtyForLine(el.getAttribute('data-line-key'));
                if (!cap || typed <= cap.current || typed <= cap.allowed) return;
                showToast(cap.byGroup ? (state.config.groupBlockMessageText || state.config.blockMessageText)
                                      : state.config.blockMessageText);
                if (cap.allowed > cap.current) {
                    el.value = String(cap.allowed);   // the theme navigates with this number
                } else {
                    el.value = String(cap.current);   // nothing to change: stop the reload
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                }
            } catch (e) { warn('cart page quantity check failed', e); }
        }, true);

        // The +/- links (href /cart/change?id=<key>&quantity=<n>).
        document.addEventListener('click', (event) => {
            try {
                const a = event.target && event.target.closest && event.target.closest('a[href*="/cart/change"]');
                if (!a || !onCartPage()) return;
                const url = new URL(a.href, location.href);
                const key = url.searchParams.get('id') || url.searchParams.get('line');
                const asked = Math.floor(Number(url.searchParams.get('quantity')));
                if (!key || !Number.isFinite(asked)) return;
                const cap = allowedQtyForLine(key);
                if (!cap || asked <= cap.current || asked <= cap.allowed) return;
                showToast(cap.byGroup ? (state.config.groupBlockMessageText || state.config.blockMessageText)
                                      : state.config.blockMessageText);
                if (cap.allowed > cap.current) {
                    url.searchParams.set('quantity', String(cap.allowed));
                    a.href = url.pathname + url.search;   // the theme reads the href after us
                } else {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                }
            } catch (e) { warn('cart page +/- check failed', e); }
        }, true);
    }

    function wireCartFormSubmit() {
        document.addEventListener('submit', (event) => {
            const form = event.target;
            if (!form || form.tagName !== 'FORM') return;
            if (!state.config.enabled) return;

            // The cart route, localized (/cart, /en/cart, /fr-ca/cart).
            let path = '';
            try { path = new URL(form.action, location.href).pathname; } catch (e) { return; }
            if (!/\/cart\/?$/.test(path)) return;

            // Our own re-submit goes through as it is.
            if (form.dataset.agbCapChecked === '1') { delete form.dataset.agbCapChecked; return; }
            const submitter = event.submitter;
            if (submitter && (submitter.name === 'checkout' || submitter.value === 'checkout')) {
                // CHECKOUT IS NEVER BLOCKED - but the quantities it carries are
                // brought down to the caps first (Dorian 2026-10-22: shoppers
                // type any number of patches on /cart and press Check out, and
                // that click was let through untouched). Fail-open: an error, or
                // no answer within CHECKOUT_CLAMP_MS, and checkout goes as typed.
                event.preventDefault();
                event.stopPropagation();
                let sent = false;
                const send = () => {
                    if (sent) return;
                    sent = true;
                    form.dataset.agbCapChecked = '1';
                    if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
                    else form.submit();
                };
                const timer = setTimeout(send, CHECKOUT_CLAMP_MS);
                (async () => {
                    try {
                        const note = await clampFormForCheckout(form, () => sent);
                        if (note && !sent) showToast(note);
                    } catch (e) {
                        warn('checkout clamp failed; checking out as typed', e);
                    }
                    clearTimeout(timer);
                    send();
                })();
                return;
            }

            // Each quantity input carries data-line-key (snippets/quantity-selector.liquid),
            // so targets resolve exactly -- no positional guessing against the snapshot.
            const inputs = Array.from(form.querySelectorAll('input[name="updates[]"]'));
            const updates = [];
            for (const el of inputs) {
                const lineKey = el.getAttribute('data-line-key');
                const targetQty = Number(el.value);
                if (lineKey && Number.isFinite(targetQty)) updates.push({ variantId: null, lineKey, targetQty });
            }
            if (!updates.length) return;

            event.preventDefault();
            event.stopPropagation();

            (async () => {
                let decision = { blocked: false };
                try {
                    decision = await decideUpdate(updates);
                } catch (e) {
                    warn('cart form check failed; letting the submit through', e);
                }
                if (decision && decision.blocked) {
                    showToast(decision.message || state.config.blockMessageText);
                    // Put the fields back to what the cart actually holds, so the
                    // page does not keep showing a quantity that was refused.
                    for (const el of inputs) {
                        const line = findSnapshotLine({ lineKey: el.getAttribute('data-line-key') });
                        if (line && Number.isFinite(line.quantity)) el.value = String(line.quantity);
                    }
                    return;
                }
                form.dataset.agbCapChecked = '1';
                if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
                else form.submit();
            })();
        }, true);
    }

    // ===== Public API =========================================================

    window.AGB_CartCap = {
        get state() { return state; },
        getConfig: () => state.config,
        refetchConfig: () => {
            localStorage.removeItem(LS_CONFIG_AT);
            return fetchConfigFromAPI();
        },
        refetchCart: () => fetchInitialCart(),
        // Manually probe: what happens if shopper tries to add this variant?
        // Returns {blocked, reason, max, handle?}.
        wouldBlock: async (variantId, quantity = 1) => {
            const handle = findHandleForVariant(variantId);
            return decideAdd([{ variantId: String(variantId), quantity, handle }]);
        }
    };

    // ===== Init ===============================================================

    function init() {
        loadCachedConfig();
        wrapFetch();
        wrapXHR();
        wireCartChangeListener();
        wireCartFormSubmit();
        wireCartPageQuantity();
        // Kick off async loads in parallel; engine works as data arrives.
        fetchConfigFromAPI();
        fetchInitialCart().then(preloadCollectionMemberships);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
